import { Injectable } from '@angular/core';
import { TechnicalProductsOpenPlatformService, ContractOP, TechnicalProductsRequestOP } from '@cemex-core/cmx-api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, withLatestFrom, take } from 'rxjs/operators';

import { OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import {
  AddedValueActionTypes,
  AddedValuesResetActionOPByFormID,
  LoadAddedValuesCompleteActionOP,
  LoadAddedValuesNotFormulatingActionOP,
  LoadTemperatureValues,
  LoadTemperatureValuesCompleted,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/actions/open-platform/added-values.actions';
import {
  getOpenPlatformProducts,
  getOpenPlatformProductsState,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/products.selectors';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { ExperiencesService } from '@ordersnproducts/modules/shared/services/experiences.service';

@Injectable()
export class AddedValuesNotFormulatingOpenPlatformEffects {
  constructor(
    private actions$: Actions,
    private store: Store<OrderBuilderState>,
    private technicalProductsOpenPlatformApi: TechnicalProductsOpenPlatformService,
    private experiencesService: ExperiencesService,
  ) {}

  temperatureEnable$ = this.experiencesService.isTemperatureEnabled$().pipe(
    take(1),
    map((isEnabled) => isEnabled !== null),
  );

  loadAddedValuesNotFormulatingOP$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddedValueActionTypes.LOAD_ADDED_VALUES_NOT_FORMULATING_OP),
      withLatestFrom(this.store.select(getOpenPlatformProductsState)),
      filter(([action, productsState]) => productsState.availableProducts.length !== 0),
      filter(([action, productsState]) => {
        const dispatchedAction: LoadAddedValuesNotFormulatingActionOP = action;
        if (!dispatchedAction.payload.addedValues) {
          // Enters here only when fetching first added values
          return true;
        } else {
          // If addedValues is defined, must contain trueable values
          // Can get undefined/null values when unselecting from combo box
          const valid = dispatchedAction.payload.addedValues.every((value) => !!value);
          if (!valid) {
            this.store.dispatch(
              new LoadAddedValuesCompleteActionOP({
                productCode: dispatchedAction.payload.productCode,
                index: dispatchedAction.payload.index,
                addedValues: null,
                technicalProduct: null,
                contractCode: null,
                formId: null,
              }),
            );
          }
          return valid;
        }
      }),
      mergeMap(([action, productState]) => {
        const dispatchedAction: LoadAddedValuesNotFormulatingActionOP = action;
        const productSelected = productState.availableProducts.find(
          (product) => product.productCode.replace(/^0+/, '') === dispatchedAction.payload.productCode,
        );
        const contract = productSelected.contracts.find(
          (item) => item.contractCode.replace(/^0+/, '') === dispatchedAction.payload.contractCode,
        );
        const contractSelected = {
          contractCode: _safe(() => contract.contractCode),
          contractXId: _safe(() => contract.contractXId),
        } as ContractOP;
        let valueAddeds = [];
        if (productSelected.hasMandatoryValueAdded === true) {
          productSelected.technicalProducts.forEach((technicalProduct) => {
            technicalProduct.valueAddeds.forEach((addedValue) => {
              if (addedValue.isMandatory === true) {
                valueAddeds.push(addedValue);
              }
            });
          });
        }
        if (dispatchedAction.payload.addedValues) {
          valueAddeds = valueAddeds.concat(dispatchedAction.payload.addedValues);
        }
        const aux: TechnicalProductsRequestOP = {
          product: productSelected,
          selectedContract: contractSelected,
          selectedValueAddeds: valueAddeds,
          combinations: productState.valueAddedsNotCombinable,
        };
        if (!!contractSelected.contractCode) {
          return this.technicalProductsOpenPlatformApi.fetch(aux).pipe(
            map((response: any) => {
              response.valueAddeds.forEach((vap) => {
                vap.valueAddedCode = vap.valueAddedCode.replace(/^0+/, '');
              });
              let enabledTemperature = false;
              this.temperatureEnable$.subscribe((isEnabled) => {
                enabledTemperature = !!isEnabled;
              });

              const vapsNotTemperature = enabledTemperature
                ? response.valueAddeds.filter((vap) => !vap.isTemperatureControl)
                : response.valueAddeds;
              return new LoadAddedValuesCompleteActionOP({
                productCode: dispatchedAction.payload.productCode,
                index: dispatchedAction.payload.index,
                addedValues: vapsNotTemperature,
                technicalProduct: response.technicalProducts,
                contractCode: dispatchedAction.payload.contractCode,
                formId: dispatchedAction.payload.formId,
              });
            }),
          );
        } else {
          throw new Error('No contractCode found in form:' + dispatchedAction.payload.formId);
        }
      }),
      catchError((error) => {
        return of(
          new AddedValuesResetActionOPByFormID({
            formId: error.message.split(':')[1],
          }),
        );
      }),
    ),
  );

  // Split temperature vaps

  loadTemperatureVaps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddedValueActionTypes.LOAD_TEMPERATURE_VALUES),
      withLatestFrom(
        this.store.select(getOpenPlatformProducts).pipe(filter((products) => !!products && products.length !== 0)),
      ),
      map(([action, products]) => {
        const dispatchedAction: LoadTemperatureValues = action;
        const selectedProduct = dispatchedAction.payload.productCode;
        const selectedContract = dispatchedAction.payload.contractCode;
        const productSelected = _safe(() =>
          products.find((product) => product.productCode.replace(/^0+/, '') === selectedProduct),
        );
        if (!productSelected) {
          return new LoadTemperatureValuesCompleted({
            addedValues: [],
            formId: dispatchedAction.payload.formId,
          });
        }
        const contract = productSelected.contracts.find(
          (item) => item.contractCode.replace(/^0+/, '') === selectedContract,
          null,
        );

        const availableAddedValues = _safe(() => contract.valueAddeds, []);
        const availableAddedValuesFilteredTemperature = availableAddedValues.filter(
          (value) => value.isTemperatureControl === true,
        );
        return new LoadTemperatureValuesCompleted({
          addedValues: availableAddedValuesFilteredTemperature,
          formId: dispatchedAction.payload.formId,
        });
      }),
    ),
  );
}
